/**
 * Outbound SMS sanitizer — Telnyx spam-blocks messages carrying "fancy font"
 * (Mathematical Alphanumeric Symbols, e.g. 𝟯𝟮𝟳𝟱 𝗡𝗪) with `40002 Blocked as
 * spam`, and invisible characters score the same way. Confirmed in prod: a
 * plain-text address delivered, the math-sans-bold copy of it did not.
 *
 * The clean is deliberately conservative: NFKC folds styled alphabets back to
 * ASCII (𝟯 → 3) and normalizes exotic spaces, invisible characters are
 * dropped, smart punctuation folds to its plain equivalent. Accents (é, ñ),
 * emoji (including ZWJ families), and lookalike homoglyphs (cyrillic а) are
 * kept — auto-folding those would corrupt legitimate names, so they only cost
 * UCS-2 pricing, which the segment counter already surfaces.
 *
 * The backend applies the same NFKC + invisible-strip on its send path for
 * every source; sanitizing here too is deliberate defense in depth, and keeps
 * what the agent sees (counter, optimistic bubble) equal to what is sent.
 */

import { PLAIN_EQUIVALENT } from './sms-cost'
import { splitGraphemes } from './sms-segments'

/** Zero-width / format characters: ZWSP, ZWNJ, ZWJ, word joiner, BOM, soft
 *  hyphen, Mongolian vowel separator. Invisible on screen, spam-scored on the
 *  wire. ZWJ is only dropped when it stands alone — inside an emoji grapheme
 *  the whole cluster is kept (see `cleanGrapheme`). */
const INVISIBLE = new Set([
  0x200b, 0x200c, 0x200d, 0x2060, 0xfeff, 0x00ad, 0x180e,
])

/** The combining marks `htmlToMultilineText` styles underline/strike with
 *  (U+0332 / U+0336) — NFKC does not fold them, so they are stripped here. */
const STYLING_MARKS = new Set([0x0332, 0x0336])

const EMOJI = /\p{Extended_Pictographic}/u

/** Keep tab/newline/CR; drop other C0 + C1 control characters. */
function keepCodePoint(cp: number): boolean {
  if (INVISIBLE.has(cp) || STYLING_MARKS.has(cp)) return false
  if (cp === 0x09 || cp === 0x0a || cp === 0x0d) return true
  return cp >= 0x20 && (cp < 0x7f || cp > 0x9f)
}

function cleanGrapheme(grapheme: string): string {
  if (grapheme in PLAIN_EQUIVALENT) return PLAIN_EQUIVALENT[grapheme]
  // Emoji clusters pass through whole — filtering would strip the ZWJ that
  // joins a family/skin-tone sequence and split it into separate emoji.
  if (EMOJI.test(grapheme)) return grapheme
  let out = ''
  for (const char of grapheme) {
    if (keepCodePoint(char.codePointAt(0) ?? 0)) out += char
  }
  return out
}

/**
 * Character-level clean with no whitespace tidying — safe for a pasted
 * fragment, where a leading/trailing space or newline is meaningful. This is
 * also the "does sanitizing change anything visible" comparison base for the
 * composer notice.
 */
export function sanitizeSmsFragment(text: string): string {
  if (!text) return ''
  const normalized = text.normalize('NFKC').replace(/[\u2028\u2029]/g, '\n')
  return splitGraphemes(normalized).map(cleanGrapheme).join('')
}

/** Full clean for an outbound SMS body: character clean plus whitespace
 *  tidying — runs of spaces/tabs collapse, 3+ blank lines collapse to one,
 *  and the result is trimmed. An all-junk input comes back empty, which the
 *  composers' has-content gates already treat as "nothing to send". */
export function sanitizeSmsText(text: string): string {
  return sanitizeSmsFragment(text)
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Clean every text node of editor/template HTML in place, leaving the markup
 *  (template chips, their `data-insert` tokens) untouched — for sanitizing a
 *  rich paste and an SMS template body at save. */
export function sanitizeSmsHtml(html: string): string {
  if (typeof document === 'undefined' || !html) return html
  const div = document.createElement('div')
  div.innerHTML = html
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  let node = walker.nextNode()
  while (node) {
    nodes.push(node as Text)
    node = walker.nextNode()
  }
  for (const text of nodes) {
    const next = sanitizeSmsFragment(text.data)
    if (next !== text.data) text.data = next
  }
  return div.innerHTML
}
